import { plainToClass } from "class-transformer";
import EshopRequestBuilder from "data/eshopRequestBuilder";
import { toJS } from "mobx";
import EshopError from "models/eshopError";
import Order from "models/order";
import PaymentRestrictionsResponse from "entities/paymentRestrictionsResponse";
import ChargeProposedPurchaseResponse from "entities/chargeProposedPurchaseResponse";
import AuthorizationService from "services/authorizationService";
import EventBus, { paymentConfirmationAvailable, paymentConfirmationError } from "services/eventBus";
import isErrorResponse from "utils/isErrorResponse";
import RepositoryBase from "./repositoryBase";
import ChargeOrder from "models/chargeOrder";
import ChargeOrderRequest from "entities/chargeOrderRequest";
import ChargeOrderResponse from "entities/chargeOrderResponse";
import GetApiV3ChargeProposedPurchaseQuery from "entities/getApiV3ChargeProposedPurchaseQuery";
import ChargeOrderCustomerRatingRequest from "entities/chargeOrderCustomerRatingRequest";
import { dateToIsoString, utcToCZZonedTime } from "utils/dateUtils";

export default class ChargeOrderRepository extends RepositoryBase<EshopRequestBuilder> {
  constructor(
    protected apiFactory: ReturnType<typeof EshopRequestBuilder.Factory>,
    protected authorizationService: AuthorizationService,
    protected eventBus: EventBus
  ) {
    super(() => apiFactory(), authorizationService, eventBus);
  }

  private handleEmptyResponse = async (response: Response) => {
    if (!response.ok) return plainToClass(EshopError, (await response.json()) as EshopError);

    return undefined;
  };
  getAll() {
    return this.callApi(api => api.all("charge_orders").get<Order[]>());
  }

  get(orderId: string) {
    return this.callApi(api =>
      api
        .apiVersion(3.1)
        .one("charge_orders", orderId)
        .get<ChargeOrderResponse>()
        .then(item => plainToClass(ChargeOrderResponse, item))
    );
  }

  getProposedPurchase({ chargeId }: GetApiV3ChargeProposedPurchaseQuery) {
    return this.callApi(api =>
      api
        .apiVersion(3.1)
        .one(`charge_proposed_purchases/${chargeId}`)
        .get<ChargeProposedPurchaseResponse>()
        .then(item => plainToClass(ChargeProposedPurchaseResponse, item))
    );
  }

  postProposedPurchaseRequest({ chargeId, proposedPurchaseRequestID }: GetApiV3ChargeProposedPurchaseQuery) {
    return this.callApi(api =>
      api
        .apiVersion(3.1)
        .one("proposed_purchase_requests")
        .post<ChargeOrderRequest | EshopError>({ proposedPurchaseRequestId: proposedPurchaseRequestID })
        .then(response => {
          if (isErrorResponse(response)) {
            throw new Error("4xx error received from server");
          } else {
            return plainToClass(ChargeProposedPurchaseResponse, response);
          }
        })
    );
  }

  getTransferPaymentRestrictions() {
    return this.callApi(api =>
      api
        .apiVersion(3)
        .one("charges/payment_restrictions")
        .get<PaymentRestrictionsResponse>()
        .then(item => plainToClass(PaymentRestrictionsResponse, item))
    );
  }

  post(chargeOrder: ChargeOrder, connectionId: string, turnstileToken: string | undefined | null) {
    const postOrder = { ...toJS(chargeOrder) } as any;
    postOrder.charges = postOrder.charges?.map((item: any) => {
      if (item.validity.validSinceDate) {
        item.validity.validSinceDate = dateToIsoString(utcToCZZonedTime(item.validity.validSinceDate));
      }
      delete item.validSince;
      return item;
    });
    delete postOrder.id;
    return this.callApi(() =>
      this.apiFactory()
        .apiVersion(3.2)
        .one("charge_orders")
        .post<ChargeOrderRequest | EshopError>(postOrder, undefined, connectionId, undefined, turnstileToken)
        .then(response => {
          if (isErrorResponse(response)) {
            throw new Error("4xx error received from server");
          } else {
            return plainToClass(ChargeOrderRequest, response);
          }
        })
    );
  }
  postCustomerRating(orderId: string, customerRating: ChargeOrderCustomerRatingRequest) {
    return this.callApi(() =>
      this.apiFactory()
        .apiVersion(3)
        .one(`charge_orders/${orderId}/customer_rating`)
        .clearPost(customerRating, undefined, undefined, undefined)
        .then(async response => await this.handleEmptyResponse(response))
    );
  }

  async getPaymentConfirmation(orderId: string) {
    return this.callApi(api =>
      api
        .apiVersion(3)
        .one(`single_charge_orders/${orderId}/receipt`)
        .get<Response>()
        .then(response => {
          if (response.ok && response.redirected) {
            this.eventBus.publish(paymentConfirmationAvailable({ url: response.url }));
            return response.url;
          }
          this.eventBus.publish(paymentConfirmationError());
          return undefined;
        })
        .catch(() => {
          this.eventBus.publish(paymentConfirmationError());
          return undefined;
        })
    );
  }
}
